import React, { useState, useEffect, useRef } from 'react';
import { TrendingUp, RefreshCw } from 'lucide-react';
import { API_BASE_URL } from '../utils/constants';
import { useLanguage } from '../contexts/LanguageContext';

const CurrencyTicker = () => {
  const { t } = useLanguage();
  // Default rates as fallback (Arbitraje: USD x Currency)
  const defaultRates = [
    { country: 'Brasil', code: 'br', currency: 'BRL', compra: 5.220, venta: 5.290, symbol: 'R$' },
    { country: 'Argentina', code: 'ar', currency: 'ARS', compra: 1470.000, venta: 1540.000, symbol: '$' },
    { country: 'Europa', code: 'eu', currency: 'EUR', compra: 1.130, venta: 1.160, symbol: '€' },
  ];

  const [rates, setRates] = useState(defaultRates);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const scrollRef = useRef(null);

  // Format number - show 3 decimals for small numbers, no decimals for large
  const formatRate = (value) => {
    if (value >= 100) {
      // Large numbers like ARS 1540 - show with thousand separator, no decimals
      return value.toLocaleString('es-PY', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    }
    // Small numbers like BRL 5.290 or EUR 1.160 - show 3 decimals
    return value.toLocaleString('es-PY', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
  };

  // Fetch live exchange rates from Cambio Bonanza via backend
  const fetchRates = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}exchange-rates/`);
      const data = await response.json();
      
      if (data && data.rates) {
        setRates(data.rates);
        setLastUpdated(new Date());
      }
    } catch (error) {
      console.error('Error fetching exchange rates:', error);
      // Keep using default/previous rates on error
    } finally {
      setLoading(false);
    }
  };

  // Fetch rates on mount and every 30 minutes
  useEffect(() => {
    fetchRates();
    const interval = setInterval(fetchRates, 30 * 60 * 1000); // 30 minutes
    return () => clearInterval(interval);
  }, []);

  // Auto-rotate every 4 seconds on mobile
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % rates.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [rates.length]);

  // Flag image component using flagcdn.com
  const FlagImage = ({ code, className = "" }) => (
    <img 
      src={`https://flagcdn.com/w40/${code}.png`}
      srcSet={`https://flagcdn.com/w80/${code}.png 2x`}
      alt={code.toUpperCase()}
      className={`inline-block rounded-sm ${className}`}
      style={{ width: '24px', height: '16px', objectFit: 'cover' }}
    />
  );

  return (
    <div className="bg-gradient-to-r from-slate-50 via-gray-50 to-slate-100 border-b border-gray-200 overflow-hidden">
      <div className="mx-auto max-w-7xl px-4">
        <div className="flex items-center justify-center gap-3 py-2 text-xs sm:text-sm font-medium">
          <TrendingUp className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0 text-emerald-600" />
          <span className="font-semibold text-gray-700 hidden sm:inline tracking-wide">{t('cotacao') || 'Cotação'}</span>
          
          {loading ? (
            <RefreshCw className="h-4 w-4 animate-spin text-gray-400" />
          ) : (
            <>
              {/* Mobile: Show one at a time with animation */}
              <div className="sm:hidden flex items-center gap-2">
                <FlagImage code={rates[currentIndex].code} />
                <span className="text-[10px] text-gray-500">{rates[currentIndex].currency}</span>
                <span className="font-semibold text-gray-800">
                  {formatRate(rates[currentIndex].venta || rates[currentIndex].rate)}
                </span>
              </div>
              
              {/* Desktop: Show all rates */}
              <div className="hidden sm:flex items-center gap-4" ref={scrollRef}>
                {rates.map((item, index) => (
                  <div key={item.currency} className="flex items-center gap-1.5">
                    {index > 0 && <span className="text-gray-300 mx-1">|</span>}
                    <FlagImage code={item.code} />
                    <span className="text-[10px] text-gray-500 uppercase tracking-wider">{item.currency}</span>
                    <span className="font-semibold text-gray-800">
                      {formatRate(item.venta || item.rate)}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CurrencyTicker;
